import React, { useCallback, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import clsx from "clsx";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FormProvider, Controller, useForm } from "react-hook-form";

import Input from "../../../../components/Input";
import SmsCost from "../../../../components/SmsCost";

import {
  QUERY_DEFAULT_MESSAGE_TEMPLATE,
  QUERY_MESSAGE_TEMPLATE,
  QUERY_MESSAGE_TEMPLATE_OPTIONS,
  QUERY_CLIENT_PREFERENCES,
} from "../../../../config/graphql/query";
import {
  CREATE_MESSAGE_TEMPLATE,
  UPDATE_MESSAGE_TEMPLATE,
  DELETE_MESSAGE_TEMPLATE,
} from "../../../../config/graphql/mutation";

import { useCurrentLanguage } from "../../../../lib/hooks/useCurrentLanguage";
import { getApiUrl } from "../../../../lib/common/getApiUrl";

import { useClientId } from "../hooks/useClient";
import useHasRole from "../../../../lib/hooks/useHasRole";

interface FieldValues
  extends Pick<IMessageTemplate, "message" | "template" | "title"> {}

const VIDEO_CALL_NAMESPACE: Record<string, keyof IMemberGroupPreferences> = {
  Guest: "visitor",
  EmployeeVisitorArrived: "meetingAttendee",
  PackageDeliveryArrived: "packageDelivery",
  FoodDeliveryArrived: "foodDelivery",
};

const VIDEO_CALL_VARIABLE = "{{videoCallUrl}}";

const MANUAL_VIDEO_CALL_VARIABLE = "{{url}}";

const DOOR_OPEN_VARIABLES = [
  "{{doorOpenUrlEn}}",
  "{{doorOpenUrlNl}}",
  "{{doorOpenUrlDe}}",
];

const DOOR_OPEN_VARIABLES_ACCEPTED = [
  ...DOOR_OPEN_VARIABLES,
  "{{doorOpenUrl}}",
];

const SHORT_LINK_SAMPLE = "/s/Ab3dK9ZqR7mT";

const FALLBACK_API_URL = "https://api.digitalreception.app";

const LOCAL_API_URL_PATTERN =
  /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i;

const shortLinkSample = () => {
  const apiUrl = getApiUrl() || "";

  const host = LOCAL_API_URL_PATTERN.test(apiUrl.replace(/\/+$/, ""))
    ? FALLBACK_API_URL
    : apiUrl;

  return `${host}${SHORT_LINK_SAMPLE}`;
};

const MessageTemplate = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const canEdit = useHasRole(["ADMIN", "CLIENT_ADMIN"]);
  const clientBasePath = isAdmin ? `/clients/${clientId}` : `/client`;

  const language = useCurrentLanguage();

  const methods = useForm<FieldValues>({
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: {},
  });

  const apolloContent = useMemo(
    () => ({
      headers: {
        ClientId: clientId,
      },
    }),
    [clientId],
  );

  const { data: messageTemplateOptionsData } = useQuery<{
    messageTemplateOptions: string[];
    availableMessageTemplateOptions: string[];
  }>(QUERY_MESSAGE_TEMPLATE_OPTIONS, {
    context: apolloContent,
  });

  const isDefault = useMemo(
    () => messageTemplateOptionsData?.messageTemplateOptions.includes(id),
    [messageTemplateOptionsData, id],
  );

  const { data } = useQuery(
    isDefault ? QUERY_DEFAULT_MESSAGE_TEMPLATE : QUERY_MESSAGE_TEMPLATE,
    {
      skip: !id,
      variables: isDefault ? { template: id } : { id },
      context: apolloContent,
    },
  );

  const template = isDefault
    ? data?.defaultMessageTemplate.template
    : data?.messageTemplate.template;

  const { data: clientPreferencesData } = useQuery<{
    client: { preferences?: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    fetchPolicy: "network-only",
  });

  const videoCallNamespace = VIDEO_CALL_NAMESPACE[template];

  const carriesVideoCall =
    !!videoCallNamespace &&
    !!clientPreferencesData?.client?.preferences?.company?.employee?.[
      videoCallNamespace
    ]?.autoChannel?.videoCall;

  const carriesDoorOpen = template === "Guest";

  const isManualVideoCall = template === "VideoCall";

  const availableVariables = useMemo(
    () => [
      "{{name}}",
      ...(template === "EmployeeVisitorArrived" ? ["{{attendeeName}}"] : []),
      ...(template === "Visitor" || template === "Guest"
        ? ["{{visitorName}}"]
        : []),
      ...(isManualVideoCall ? [MANUAL_VIDEO_CALL_VARIABLE] : []),
      ...(carriesVideoCall ? [VIDEO_CALL_VARIABLE] : []),
      ...(carriesDoorOpen ? DOOR_OPEN_VARIABLES : []),
    ],
    [template, isManualVideoCall, carriesVideoCall, carriesDoorOpen],
  );

  const linkPreview = useCallback(
    (message?: string | null) => {
      if (
        !message ||
        !(carriesVideoCall || carriesDoorOpen || isManualVideoCall)
      ) {
        return undefined;
      }

      const url = shortLinkSample();

      const withLink = (text: string, variables: string[], key: string) => {
        const used = variables.filter((variable) => text.includes(variable));

        if (!used.length) {
          return `${text}\n${t(key, { url })}`;
        }

        return used.reduce(
          (filled, variable, index) =>
            filled.split(variable).join(index === 0 ? url : ""),
          text,
        );
      };

      let preview = message;

      if (isManualVideoCall) {
        preview = preview.split(MANUAL_VIDEO_CALL_VARIABLE).join(url);
      }

      if (carriesVideoCall) {
        preview = withLink(
          preview,
          [VIDEO_CALL_VARIABLE],
          "client:client.twilioMessage.cost.videoLine",
        );
      }

      if (carriesDoorOpen) {
        preview = withLink(
          preview,
          DOOR_OPEN_VARIABLES_ACCEPTED,
          "client:client.twilioMessage.cost.doorLine",
        );
      }

      return preview;
    },
    [carriesVideoCall, carriesDoorOpen, isManualVideoCall, t],
  );

  useEffect(() => {
    if (data?.messageTemplate || data?.defaultMessageTemplate) {
      methods.reset(
        isDefault ? data?.defaultMessageTemplate : data?.messageTemplate,
      );
    }
  }, [data, methods]);

  const [onUpdate] = useMutation<{ updateMessageTemplate: IMessageTemplate }>(
    UPDATE_MESSAGE_TEMPLATE,
    {
      refetchQueries: [
        {
          query: QUERY_MESSAGE_TEMPLATE,
          variables: { id },
          context: apolloContent,
        },
      ],
      context: apolloContent,
    },
  );

  const [onCreate] = useMutation<{ createMessageTemplate: IMessageTemplate }>(
    CREATE_MESSAGE_TEMPLATE,
    {
      context: apolloContent,
    },
  );

  const [onDelete] = useMutation(DELETE_MESSAGE_TEMPLATE, {
    context: apolloContent,
  });

  const onBeforeDelete = useCallback(() => {
    return onDelete({ variables: { id } })
      .then(({ data }) => {
        toast.success<string>(
          t("client:client.twilioMessage.revertToDefault.toast"),
        );
        setShow(false);
        history.replace(
          `${clientBasePath}/twilio-message-templates/${data?.deleteMessageTemplate.template}`,
        );
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );

        history.replace(`${clientBasePath}/message-templates`);
      });
  }, [history, onDelete, clientId, id, clientBasePath, t]);

  const onSubmit = useCallback(
    (values: FieldValues) => {
      const input = {
        ...(!isDefault ? { id } : {}),
        ...values,
      };

      if (!isDefault) {
        return onUpdate({ variables: { input } })
          .then(({ data }) => {
            methods.reset(data?.updateMessageTemplate);
          })
          .then(() => {
            toast.success<string>(t("client:client.information.toast.updated"));
          })
          .catch((error) => {
            toast.error<string>(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message,
            );
          });
      }

      return onCreate({ variables: { input } })
        .then(({ data }) => {
          toast.success<string>(t("client:client.information.toast.created"));

          history.replace(
            `${clientBasePath}/twilio-message-templates/${data?.createMessageTemplate?.id}`,
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [clientId, history, id, methods, onCreate, onUpdate, clientBasePath, t],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          {isAdmin && (
            <li className="breadcrumb-item active" aria-current="page">
              <Link to="/clients">{t("client:client.token.nav.clients")}</Link>
            </li>
          )}
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={clientBasePath}>
              {data?.messageTemplate?.client?.title ?? "Client"}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`${clientBasePath}/twilio-message-templates`}>
              {t("client:client.twilioMessage.nav.twilioMessageTemplates")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {data?.messageTemplate?.title ?? "Title"}
          </li>
        </ol>
      </nav>
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-12 col-md-6 col-lg-8">
            <div className="form-group">
              <label htmlFor="title">
                {t("client:client.twilioMessage.form.title")}
              </label>
              <Input
                name="title"
                rules={{
                  required: {
                    value: true,
                    message: t("client:client.twilioMessage.error.required"),
                  },
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="type">
                {t("client:client.twilioMessage.form.template")}
              </label>
              <Input name="template" disabled />
            </div>
            <div className="form-group">
              <label htmlFor="templateId">
                {t("client:client.twilioMessage.form.message")}
              </label>
              <Controller
                name="message"
                rules={{
                  required: {
                    value: true,
                    message: t("client:client.twilioMessage.error.required"),
                  },
                }}
                render={({
                  field: { name, ...rest },
                  fieldState: { error },
                }) => (
                  <>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span
                          className="input-group-text"
                          id={`translate-${name}`}
                        >
                          {`${language}`}
                        </span>
                      </div>
                      <textarea
                        {...rest}
                        rows={5}
                        aria-describedby={`translate-${name}`}
                        className={clsx("form-control", {
                          "is-invalid": !!error,
                        })}
                      />
                    </div>
                    <SmsCost
                      value={rest.value}
                      withLink={linkPreview(rest.value)}
                    />
                    <div className="mt-2">
                      <div className="small text-muted">
                        {t("client:client.twilioMessage.cost.variablesLabel")}
                      </div>
                      <div className="d-flex flex-wrap align-items-center mt-1">
                        {availableVariables.map((variable) => (
                          <code
                            key={variable}
                            className="small bg-light border rounded px-1 mr-1 mb-1"
                          >
                            {variable}
                          </code>
                        ))}
                      </div>
                    </div>
                    {carriesVideoCall && (
                      <div className="small text-muted mt-1">
                        {t("client:client.twilioMessage.cost.linkHintVideo")}
                      </div>
                    )}
                    {carriesDoorOpen && (
                      <div className="small text-muted mt-1">
                        {t("client:client.twilioMessage.cost.linkHintDoor")}
                      </div>
                    )}
                    {(carriesVideoCall || carriesDoorOpen) && (
                      <div className="small text-muted mt-1">
                        {t("client:client.twilioMessage.cost.linkHint")}
                      </div>
                    )}
                    {!!error && (
                      <div className="invalid-feedback">{error?.message}</div>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className="col-12">
            <input
              type="submit"
              className="btn btn-primary"
              disabled={!canEdit}
            />
            {!isDefault && canEdit && (
              <button
                onClick={(e) => {
                  e.preventDefault();

                  setShow(true);
                }}
                className="btn btn-danger ml-3"
              >
                {t("client:client.twilioMessage.revertToDefault.button")}
              </button>
            )}
          </div>
        </form>
      </FormProvider>
      {!isDefault && (
        <Modal
          show={show}
          onHide={setShow}
          centered
          aria-labelledby="contained-modal-title-vcenter"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("client:client.twilioMessage.revertToDefault.modal.title")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t("client:client.twilioMessage.revertToDefault.modal.body", {
              title: data?.messageTemplate?.client?.title,
            })}
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" onClick={() => setShow(false)}>
              {t("common:cancel")}
            </Button>
            <Button size="sm" variant="danger" onClick={onBeforeDelete}>
              {t("client:client.twilioMessage.revertToDefault.button")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
});

export default MessageTemplate;
